import React, { useMemo, useState } from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaClone,
  FaCode,
  FaColumns,
  FaHeading,
  FaImage,
  FaInfoCircle,
  FaLink,
  FaMinus,
  FaParagraph,
  FaQuoteLeft,
  FaRegSquare,
  FaTrash,
} from "react-icons/fa";

import MediaPickerModal from "../MediaPickerModal";
import { toAbsolutePublicUrl } from "../../../../../services/mediaLibraryServices";
import { BLOCK_LABELS } from "./pageBuilderUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950";

const textareaClass =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm leading-7 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950";

const selectClass =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950";

export default function PageBlockEditor({
  block,
  index,
  total,
  onChange,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onRemove,
}) {
  const label = BLOCK_LABELS[block.type] || block.type;

  const icon = useMemo(() => getBlockIcon(block.type), [block.type]);

  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/70 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950">
            {icon}
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold">{label}</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
                Bloc {index + 1}/{total}
              </span>
            </div>

            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {getBlockDescription(block.type)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ActionButton
            title="Monter"
            onClick={onMoveUp}
            disabled={index === 0}
          >
            <FaArrowUp />
          </ActionButton>

          <ActionButton
            title="Descendre"
            onClick={onMoveDown}
            disabled={index >= total - 1}
          >
            <FaArrowDown />
          </ActionButton>

          <ActionButton title="Dupliquer" onClick={onDuplicate}>
            <FaClone />
          </ActionButton>

          <button
            type="button"
            onClick={onRemove}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-rose-50 px-3 text-xs font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-950/70"
          >
            <FaTrash />
            Supprimer
          </button>
        </div>
      </div>

      <div className="p-4 md:p-5">
        {block.type === "title" ? (
          <TitleEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "subtitle" ? (
          <SubtitleEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "paragraph" ? (
          <ParagraphEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "image" ? (
          <ImageEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "button" ? (
          <ButtonEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "info" ? (
          <InfoEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "quote" ? (
          <QuoteEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "twoColumns" ? (
          <TwoColumnsEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "cards" ? (
          <CardsEditor block={block} onChange={onChange} />
        ) : null}

        {block.type === "divider" ? <DividerEditor /> : null}

        {block.type === "legacyHtml" ? (
          <LegacyHtmlEditor block={block} onChange={onChange} />
        ) : null}
      </div>
    </article>
  );
}

function Field({ label, help, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      {label ? (
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </span>
      ) : null}

      {children}

      {help ? (
        <span className="mt-1.5 block text-xs text-slate-500 dark:text-slate-400">
          {help}
        </span>
      ) : null}
    </label>
  );
}

function ActionButton({ children, title, onClick, disabled = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      {children}
    </button>
  );
}

function MediaUrlField({
  label = "Image",
  value = "",
  onChange,
  altValue = "",
  onAltChange,
  onMediaSelect,
  help = "",
}) {
  const [open, setOpen] = useState(false);

  const handleSelect = (media) => {
    if (!media?.url) return;

    const absoluteUrl = toAbsolutePublicUrl(media.url);
    const suggestedAlt = media.alt_text || media.title || media.file_name || "";

    if (typeof onMediaSelect === "function") {
      onMediaSelect({
        url: absoluteUrl,
        alt: altValue || suggestedAlt,
        media,
      });
    } else {
      onChange?.(absoluteUrl);

      if (onAltChange && !altValue) {
        onAltChange(suggestedAlt);
      }
    }

    setOpen(false);
  };

  return (
    <>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(220px,0.45fr)]">
        <Field label={label} help={help}>
          <div className="flex gap-2">
            <input
              value={value || ""}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder="/uploads/... ou https://..."
              className={inputClass}
            />

            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex shrink-0 items-center justify-center rounded-2xl border border-sky-200 bg-sky-50 px-4 text-sky-700 transition hover:bg-sky-100 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200"
              title="Choisir dans la photothèque"
            >
              <FaImage />
            </button>
          </div>
        </Field>

        <Field label="Texte alternatif">
          <input
            value={altValue || ""}
            onChange={(e) => onAltChange?.(e.target.value)}
            placeholder="Description courte"
            className={inputClass}
          />
        </Field>
      </div>

      <MediaPickerModal
        open={open}
        onClose={() => setOpen(false)}
        onSelect={handleSelect}
        defaultCategory="pages"
      />
    </>
  );
}

function TitleEditor({ block, onChange }) {
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
      <Field label="Titre">
        <input
          value={block.content || ""}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          className={inputClass}
          placeholder="Titre de votre section"
        />
      </Field>

      <Field label="Alignement">
        <select
          value={block.align || "left"}
          onChange={(e) => onChange({ ...block, align: e.target.value })}
          className={selectClass}
        >
          <option value="left">Gauche</option>
          <option value="center">Centré</option>
          <option value="right">Droite</option>
        </select>
      </Field>
    </div>
  );
}

function SubtitleEditor({ block, onChange }) {
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
      <Field label="Sous-titre">
        <input
          value={block.content || ""}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          className={inputClass}
          placeholder="Votre sous-titre"
        />
      </Field>

      <Field label="Alignement">
        <select
          value={block.align || "left"}
          onChange={(e) => onChange({ ...block, align: e.target.value })}
          className={selectClass}
        >
          <option value="left">Gauche</option>
          <option value="center">Centré</option>
          <option value="right">Droite</option>
        </select>
      </Field>
    </div>
  );
}

function ParagraphEditor({ block, onChange }) {
  return (
    <Field
      label="Texte"
      help="Astuce : laissez une ligne vide pour créer un nouveau paragraphe."
    >
      <textarea
        rows={7}
        value={block.content || ""}
        onChange={(e) => onChange({ ...block, content: e.target.value })}
        className={textareaClass}
        placeholder="Votre texte..."
      />
    </Field>
  );
}

function ImageEditor({ block, onChange }) {
  const patchBlock = (patch) => {
    onChange({
      ...block,
      ...patch,
    });
  };

  return (
    <div className="space-y-5">
      <MediaUrlField
        label="URL de l’image"
        value={block.src || ""}
        onChange={(src) => patchBlock({ src })}
        altValue={block.alt || ""}
        onAltChange={(alt) => patchBlock({ alt })}
        onMediaSelect={({ url, alt }) => {
          patchBlock({
            src: url,
            alt: block.alt || alt || "",
          });
        }}
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Field label="Largeur">
          <select
            value={block.size || "wide"}
            onChange={(e) => patchBlock({ size: e.target.value })}
            className={selectClass}
          >
            <option value="small">Petite</option>
            <option value="medium">Moyenne</option>
            <option value="wide">Grande</option>
          </select>
        </Field>

        <Field label="Format">
          <select
            value={block.ratio || "auto"}
            onChange={(e) => patchBlock({ ratio: e.target.value })}
            className={selectClass}
          >
            <option value="auto">Format original</option>
            <option value="square">Carré 1:1</option>
            <option value="16-9">Bannière 16:9</option>
            <option value="4-3">Paysage 4:3</option>
            <option value="3-2">Paysage 3:2</option>
            <option value="4-5">Portrait 4:5</option>
            <option value="3-4">Portrait 3:4</option>
          </select>
        </Field>

        <Field label="Ajustement">
          <select
            value={block.fit || "cover"}
            onChange={(e) => patchBlock({ fit: e.target.value })}
            className={selectClass}
          >
            <option value="cover">Remplir / recadrer</option>
            <option value="contain">Voir l’image entière</option>
          </select>
        </Field>

        <Field label="Légende">
          <input
            value={block.caption || ""}
            onChange={(e) => patchBlock({ caption: e.target.value })}
            className={inputClass}
            placeholder="Optionnel"
          />
        </Field>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
        <div className="mb-4 flex flex-col gap-1">
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            Recadrage de l’image
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Utilisez un format carré, bannière ou portrait, puis déplacez le
            point de cadrage avec les curseurs. Le fichier original n’est pas
            modifié.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label={`Position horizontale : ${block.cropX ?? 50}%`}>
            <input
              type="range"
              min="0"
              max="100"
              value={Number.isFinite(Number(block.cropX)) ? block.cropX : 50}
              onChange={(e) => patchBlock({ cropX: Number(e.target.value) })}
              className="w-full accent-sky-600"
            />
          </Field>

          <Field label={`Position verticale : ${block.cropY ?? 50}%`}>
            <input
              type="range"
              min="0"
              max="100"
              value={Number.isFinite(Number(block.cropY)) ? block.cropY : 50}
              onChange={(e) => patchBlock({ cropY: Number(e.target.value) })}
              className="w-full accent-sky-600"
            />
          </Field>
        </div>
      </div>

      {block.src ? (
        <ImagePreview block={block} />
      ) : (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
          Choisissez une image pour afficher l’aperçu du recadrage.
        </div>
      )}
    </div>
  );
}

function ImagePreview({ block }) {
  const ratio = getPreviewRatio(block.ratio || "auto");
  const fit = block.fit === "contain" ? "contain" : "cover";
  const cropX = Number.isFinite(Number(block.cropX)) ? Number(block.cropX) : 50;
  const cropY = Number.isFinite(Number(block.cropY)) ? Number(block.cropY) : 50;

  if (ratio) {
    return (
      <div
        className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 dark:bg-slate-950"
        style={{ aspectRatio: ratio }}
      >
        <img
          src={block.src}
          alt={block.alt || ""}
          className="h-full w-full"
          style={{
            objectFit: fit,
            objectPosition: `${cropX}% ${cropY}%`,
          }}
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <img
        src={block.src}
        alt={block.alt || ""}
        className="max-h-[420px] w-full"
        style={{
          objectFit: fit,
          objectPosition: `${cropX}% ${cropY}%`,
        }}
      />
    </div>
  );
}

function getPreviewRatio(ratio = "auto") {
  switch (ratio) {
    case "square":
      return "1 / 1";

    case "16-9":
      return "16 / 9";

    case "4-3":
      return "4 / 3";

    case "3-2":
      return "3 / 2";

    case "4-5":
      return "4 / 5";

    case "3-4":
      return "3 / 4";

    default:
      return "";
  }
}

function ButtonEditor({ block, onChange }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Texte du bouton">
        <input
          value={block.label || ""}
          onChange={(e) => onChange({ ...block, label: e.target.value })}
          className={inputClass}
          placeholder="En savoir plus"
        />
      </Field>

      <Field label="Lien">
        <div className="relative">
          <FaLink className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={block.url || ""}
            onChange={(e) => onChange({ ...block, url: e.target.value })}
            className={`${inputClass} pl-11`}
            placeholder="/contact ou https://..."
          />
        </div>
      </Field>

      <Field label="Alignement">
        <select
          value={block.align || "center"}
          onChange={(e) => onChange({ ...block, align: e.target.value })}
          className={selectClass}
        >
          <option value="left">Gauche</option>
          <option value="center">Centré</option>
          <option value="right">Droite</option>
        </select>
      </Field>

      <Field label="Style">
        <select
          value={block.style || "primary"}
          onChange={(e) => onChange({ ...block, style: e.target.value })}
          className={selectClass}
        >
          <option value="primary">Bleu</option>
          <option value="secondary">Clair</option>
          <option value="dark">Foncé</option>
        </select>
      </Field>
    </div>
  );
}

function InfoEditor({ block, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
        <Field label="Titre">
          <input
            value={block.title || ""}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            className={inputClass}
            placeholder="Information importante"
          />
        </Field>

        <Field label="Couleur">
          <select
            value={block.tone || "blue"}
            onChange={(e) => onChange({ ...block, tone: e.target.value })}
            className={selectClass}
          >
            <option value="blue">Bleu</option>
            <option value="green">Vert</option>
            <option value="amber">Orange</option>
            <option value="rose">Rouge</option>
          </select>
        </Field>
      </div>

      <Field label="Contenu">
        <textarea
          rows={5}
          value={block.content || ""}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          className={textareaClass}
          placeholder="Votre information..."
        />
      </Field>
    </div>
  );
}

function QuoteEditor({ block, onChange }) {
  return (
    <div className="space-y-4">
      <Field label="Citation">
        <textarea
          rows={4}
          value={block.content || ""}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          className={textareaClass}
          placeholder="Votre citation..."
        />
      </Field>

      <Field label="Auteur / source">
        <input
          value={block.author || ""}
          onChange={(e) => onChange({ ...block, author: e.target.value })}
          className={inputClass}
          placeholder="Optionnel"
        />
      </Field>
    </div>
  );
}

function TwoColumnsEditor({ block, onChange }) {
  return (
    <div className="space-y-5">
      <Field label="Titre de la section">
        <input
          value={block.title || ""}
          onChange={(e) => onChange({ ...block, title: e.target.value })}
          className={inputClass}
          placeholder="Optionnel"
        />
      </Field>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
          <h4 className="mb-4 text-sm font-bold">Colonne gauche</h4>

          <div className="space-y-4">
            <Field label="Titre">
              <input
                value={block.leftTitle || ""}
                onChange={(e) =>
                  onChange({ ...block, leftTitle: e.target.value })
                }
                className={inputClass}
              />
            </Field>

            <Field label="Texte">
              <textarea
                rows={5}
                value={block.leftText || ""}
                onChange={(e) =>
                  onChange({ ...block, leftText: e.target.value })
                }
                className={textareaClass}
              />
            </Field>

            <MediaUrlField
              label="Image"
              value={block.leftImage || ""}
              onChange={(leftImage) => onChange({ ...block, leftImage })}
              altValue={block.leftImageAlt || ""}
              onAltChange={(leftImageAlt) =>
                onChange({ ...block, leftImageAlt })
              }
              onMediaSelect={({ url, alt }) =>
                onChange({
                  ...block,
                  leftImage: url,
                  leftImageAlt: block.leftImageAlt || alt || "",
                })
              }
            />
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
          <h4 className="mb-4 text-sm font-bold">Colonne droite</h4>

          <div className="space-y-4">
            <Field label="Titre">
              <input
                value={block.rightTitle || ""}
                onChange={(e) =>
                  onChange({ ...block, rightTitle: e.target.value })
                }
                className={inputClass}
              />
            </Field>

            <Field label="Texte">
              <textarea
                rows={5}
                value={block.rightText || ""}
                onChange={(e) =>
                  onChange({ ...block, rightText: e.target.value })
                }
                className={textareaClass}
              />
            </Field>

            <MediaUrlField
              label="Image"
              value={block.rightImage || ""}
              onChange={(rightImage) => onChange({ ...block, rightImage })}
              altValue={block.rightImageAlt || ""}
              onAltChange={(rightImageAlt) =>
                onChange({ ...block, rightImageAlt })
              }
              onMediaSelect={({ url, alt }) =>
                onChange({
                  ...block,
                  rightImage: url,
                  rightImageAlt: block.rightImageAlt || alt || "",
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CardsEditor({ block, onChange }) {
  const items = Array.isArray(block.items) ? block.items : [];

  const updateItem = (index, patch) => {
    const nextItems = [...items];

    nextItems[index] = {
      ...nextItems[index],
      ...patch,
    };

    onChange({
      ...block,
      items: nextItems,
    });
  };

  const addItem = () => {
    onChange({
      ...block,
      items: [
        ...items,
        {
          title: "Nouvelle carte",
          text: "Description courte.",
          image: "",
          imageAlt: "",
          imageRatio: "16-9",
          imageFit: "contain",
          imageCropX: 50,
          imageCropY: 50,
          linkLabel: "",
          linkUrl: "",
        },
      ],
    });
  };

  const removeItem = (index) => {
    onChange({
      ...block,
      items: items.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
        <Field label="Titre de la section">
          <input
            value={block.title || ""}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            className={inputClass}
          />
        </Field>

        <Field label="Colonnes">
          <select
            value={block.columns || 3}
            onChange={(e) =>
              onChange({ ...block, columns: Number(e.target.value) || 3 })
            }
            className={selectClass}
          >
            <option value={2}>2 colonnes</option>
            <option value={3}>3 colonnes</option>
            <option value={4}>4 colonnes</option>
          </select>
        </Field>
      </div>

      <Field label="Sous-titre">
        <input
          value={block.subtitle || ""}
          onChange={(e) => onChange({ ...block, subtitle: e.target.value })}
          className={inputClass}
          placeholder="Optionnel"
        />
      </Field>

      <div className="space-y-4">
        {items.map((item, index) => (
          <div
            key={index}
            className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-sm font-bold">Carte {index + 1}</h4>

              <button
                type="button"
                onClick={() => removeItem(index)}
                className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-200"
              >
                Supprimer
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Titre">
                  <input
                    value={item.title || ""}
                    onChange={(e) =>
                      updateItem(index, { title: e.target.value })
                    }
                    className={inputClass}
                  />
                </Field>

                <Field label="Texte court">
                  <input
                    value={item.text || ""}
                    onChange={(e) =>
                      updateItem(index, { text: e.target.value })
                    }
                    className={inputClass}
                  />
                </Field>
              </div>

              <MediaUrlField
                label="Image"
                value={item.image || ""}
                onChange={(image) => updateItem(index, { image })}
                altValue={item.imageAlt || ""}
                onAltChange={(imageAlt) => updateItem(index, { imageAlt })}
                onMediaSelect={({ url, alt }) =>
                  updateItem(index, {
                    image: url,
                    imageAlt: item.imageAlt || alt || "",
                    imageRatio: item.imageRatio || "16-9",
                    imageFit: item.imageFit || "contain",
                    imageCropX: Number.isFinite(Number(item.imageCropX))
                      ? Number(item.imageCropX)
                      : 50,
                    imageCropY: Number.isFinite(Number(item.imageCropY))
                      ? Number(item.imageCropY)
                      : 50,
                  })
                }
              />

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="Format image">
                  <select
                    value={item.imageRatio || "16-9"}
                    onChange={(e) =>
                      updateItem(index, { imageRatio: e.target.value })
                    }
                    className={selectClass}
                  >
                    <option value="16-9">Bannière 16:9</option>
                    <option value="4-3">Paysage 4:3</option>
                    <option value="3-2">Paysage 3:2</option>
                    <option value="square">Carré 1:1</option>
                    <option value="4-5">Portrait 4:5</option>
                    <option value="3-4">Portrait 3:4</option>
                  </select>
                </Field>

                <Field label="Affichage">
                  <select
                    value={item.imageFit || "contain"}
                    onChange={(e) =>
                      updateItem(index, { imageFit: e.target.value })
                    }
                    className={selectClass}
                  >
                    <option value="contain">Image entière</option>
                    <option value="cover">Remplir / recadrer</option>
                  </select>
                </Field>

                <Field label={`Horizontal : ${item.imageCropX ?? 50}%`}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={
                      Number.isFinite(Number(item.imageCropX))
                        ? item.imageCropX
                        : 50
                    }
                    onChange={(e) =>
                      updateItem(index, {
                        imageCropX: Number(e.target.value),
                      })
                    }
                    className="w-full accent-sky-600"
                  />
                </Field>

                <Field label={`Vertical : ${item.imageCropY ?? 50}%`}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={
                      Number.isFinite(Number(item.imageCropY))
                        ? item.imageCropY
                        : 50
                    }
                    onChange={(e) =>
                      updateItem(index, {
                        imageCropY: Number(e.target.value),
                      })
                    }
                    className="w-full accent-sky-600"
                  />
                </Field>
              </div>

              {item.image ? <CardImagePreview item={item} /> : null}

              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Texte du lien">
                  <input
                    value={item.linkLabel || ""}
                    onChange={(e) =>
                      updateItem(index, { linkLabel: e.target.value })
                    }
                    className={inputClass}
                    placeholder="Optionnel"
                  />
                </Field>

                <Field label="URL du lien">
                  <input
                    value={item.linkUrl || ""}
                    onChange={(e) =>
                      updateItem(index, { linkUrl: e.target.value })
                    }
                    className={inputClass}
                    placeholder="/contact ou https://..."
                  />
                </Field>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addItem}
        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        + Ajouter une carte
      </button>
    </div>
  );
}

function CardImagePreview({ item }) {
  const ratio = getPreviewRatio(item.imageRatio || "16-9");
  const fit = item.imageFit === "cover" ? "cover" : "contain";
  const cropX = Number.isFinite(Number(item.imageCropX))
    ? Number(item.imageCropX)
    : 50;
  const cropY = Number.isFinite(Number(item.imageCropY))
    ? Number(item.imageCropY)
    : 50;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Aperçu image de la carte
      </div>

      <div
        className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-950"
        style={{ aspectRatio: ratio || "16 / 9" }}
      >
        <img
          src={item.image}
          alt={item.imageAlt || item.title || ""}
          className="h-full w-full"
          style={{
            objectFit: fit,
            objectPosition: `${cropX}% ${cropY}%`,
          }}
        />
      </div>
    </div>
  );
}

function DividerEditor() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
      Ce bloc ajoute simplement une ligne de séparation dans la page.
    </div>
  );
}

function LegacyHtmlEditor({ block, onChange }) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        Cette page contient un ancien contenu HTML. Vous pouvez le conserver, le
        modifier, ou supprimer ce bloc pour reconstruire la page avec les
        nouveaux blocs simplifiés.
      </div>

      <Field label="HTML existant">
        <textarea
          rows={12}
          value={block.html || ""}
          onChange={(e) => onChange({ ...block, html: e.target.value })}
          className={`${textareaClass} font-mono text-xs`}
        />
      </Field>
    </div>
  );
}

function getBlockIcon(type) {
  switch (type) {
    case "title":
      return <FaHeading />;

    case "subtitle":
      return <FaRegSquare />;

    case "paragraph":
      return <FaParagraph />;

    case "image":
      return <FaImage />;

    case "button":
      return <FaLink />;

    case "info":
      return <FaInfoCircle />;

    case "quote":
      return <FaQuoteLeft />;

    case "twoColumns":
      return <FaColumns />;

    case "cards":
      return <FaRegSquare />;

    case "divider":
      return <FaMinus />;

    case "legacyHtml":
      return <FaCode />;

    default:
      return <FaParagraph />;
  }
}

function getBlockDescription(type) {
  switch (type) {
    case "title":
      return "Un grand titre pour structurer la page.";

    case "subtitle":
      return "Un titre secondaire plus discret.";

    case "paragraph":
      return "Un texte simple avec paragraphes automatiques.";

    case "image":
      return "Une image avec largeur, format et recadrage.";

    case "button":
      return "Un bouton avec lien vers une autre page ou un site.";

    case "info":
      return "Un encart coloré pour mettre en avant une information.";

    case "quote":
      return "Une citation ou phrase importante.";

    case "twoColumns":
      return "Deux colonnes simples avec texte et image optionnelle.";

    case "cards":
      return "Une grille de cartes pour présenter plusieurs éléments.";

    case "divider":
      return "Une séparation visuelle entre deux parties.";

    case "legacyHtml":
      return "Ancien contenu récupéré depuis l’éditeur précédent.";

    default:
      return "Bloc de contenu.";
  }
}
